// ./src/components/features/EmailLayouts/EmailLayoutsEditModal/EmailLayoutsEditModal.tsx
import React, { useState, useEffect } from "react";
import {
  Stack,
  TextInput,
  Textarea,
  Text,
  Code,
  Alert,
  Skeleton,
  Box,
} from "@mantine/core";
import { IconInfoCircle } from "@tabler/icons-react";
import { EmailLayout, EmailLayoutInput, isSystemLayout } from "@/utils/api";
import { AppDrawer } from "@/components/generic";

export interface EmailLayoutsEditModalProps {
  /** Whether the modal is open */
  opened: boolean;

  /** Function to close the modal */
  onClose: () => void;

  /** Email layout to edit (if null/undefined, it's create mode) */
  emailLayout?: EmailLayout | null;

  /** Callback when email layout should be created */
  onCreateEmailLayout?: (data: EmailLayoutInput) => Promise<EmailLayout | null>;

  /** Callback when email layout should be updated */
  onUpdateEmailLayout?: (
    id: string,
    data: EmailLayoutInput
  ) => Promise<EmailLayout | null>;

  /** Whether create operation is in progress */
  isCreating?: boolean;

  /** Whether update operation is in progress */
  isUpdating?: boolean;

  /** Whether data is being loaded */
  isLoading?: boolean;

  /** Last error from operations */
  lastError?: string | null;

  /** Callback to clear errors */
  onClearError?: () => void;
}

interface EmailLayoutFormData {
  name: string;
  description: string;
  htmlContent: string;
  textContent: string;
  fromName: string;
  fromEmail: string;
  replyToEmail: string;
  status: number;
}

const initialFormData: EmailLayoutFormData = {
  name: "",
  description: "",
  htmlContent: "",
  textContent: "",
  fromName: "",
  fromEmail: "",
  replyToEmail: "",
  status: 100,
};

const defaultHtmlTemplate = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>{{subject}}</title>
  <style>
    body {
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
      line-height: 1.6;
      color: #333;
      max-width: 600px;
      margin: 0 auto;
      padding: 20px;
    }
  </style>
</head>
<body>
  {{messageBody}}
  <footer style="margin-top: 40px; padding-top: 20px; border-top: 1px solid #eee; font-size: 12px; color: #666;">
    <p>&copy; {{currentYear}} Your Company. All rights reserved.</p>
  </footer>
</body>
</html>`;

// Loading skeleton component
const FormSkeleton: React.FC = () => (
  <Stack gap="md">
    {/* Layout Name skeleton */}
    <Box>
      <Skeleton height={14} width={100} mb={8} />
      <Skeleton height={42} radius="sm" />
    </Box>

    {/* Description skeleton */}
    <Box>
      <Skeleton height={14} width={80} mb={8} />
      <Skeleton height={80} radius="sm" />
    </Box>

    {/* Alert skeleton */}
    <Skeleton height={48} radius="sm" />

    {/* HTML Content skeleton */}
    <Box>
      <Skeleton height={14} width={90} mb={8} />
      <Skeleton height={240} radius="sm" />
    </Box>

    {/* Plain Text Content skeleton */}
    <Box>
      <Skeleton height={14} width={120} mb={8} />
      <Skeleton height={120} radius="sm" />
      <Skeleton height={12} width={240} mt={4} />
    </Box>
  </Stack>
);

export const EmailLayoutsEditModal: React.FC<EmailLayoutsEditModalProps> = ({
  opened,
  onClose,
  emailLayout,
  onCreateEmailLayout,
  onUpdateEmailLayout,
  isCreating = false,
  isUpdating = false,
  isLoading = false,
  lastError,
  onClearError,
}) => {
  const [formData, setFormData] =
    useState<EmailLayoutFormData>(initialFormData);
  const [validationErrors, setValidationErrors] = useState<
    Record<string, string | null | undefined>
  >({});

  const isEditMode = Boolean(emailLayout) && Boolean(emailLayout?.id);
  const isSystemLayoutItem = emailLayout ? isSystemLayout(emailLayout) : false;
  const isSubmitting = isCreating || isUpdating;

  // Reset form when modal opens/closes or email layout changes
  useEffect(() => {
    if (opened) {
      if (emailLayout) {
        // Edit mode - populate form with email layout data
        setFormData({
          name: emailLayout.name || "",
          description: emailLayout.description || "",
          htmlContent: emailLayout.htmlContent || "",
          textContent: emailLayout.textContent || "",
          fromName: emailLayout.fromName || "",
          fromEmail: emailLayout.fromEmail || "",
          replyToEmail: emailLayout.replyToEmail || "",
          status: emailLayout.status ?? 100,
        });
      } else {
        // Create mode - use defaults with template
        setFormData({
          ...initialFormData,
          htmlContent: defaultHtmlTemplate,
        });
      }

      setValidationErrors({});
      onClearError?.();
    }
  }, [opened, emailLayout, onClearError]);

  // Validate form
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    // Required fields
    if (!formData.name.trim()) {
      newErrors.name = "Layout name is required";
    }

    if (!formData.htmlContent.trim()) {
      newErrors.htmlContent = "HTML content is required";
    } else if (!formData.htmlContent.includes("{{messageBody}}")) {
      newErrors.htmlContent =
        "HTML content must include {{messageBody}} placeholder";
    }

    // Email validation
    if (
      formData.fromEmail &&
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.fromEmail)
    ) {
      newErrors.fromEmail = "Please enter a valid email address";
    }

    if (
      formData.replyToEmail &&
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.replyToEmail)
    ) {
      newErrors.replyToEmail = "Please enter a valid email address";
    }

    setValidationErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle field changes
  const handleFieldChange = (field: keyof EmailLayoutFormData, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear validation error when user starts typing
    if (validationErrors[field]) {
      setValidationErrors((prev) => ({
        ...prev,
        [field]: undefined,
      }));
    }
  };

  // Handle save
  const handleSubmit = async () => {
    onClearError?.();

    if (!validateForm()) {
      return;
    }

    try {
      const submitData: EmailLayoutInput = {
        name: formData.name.trim(),
        description: formData.description.trim() || null,
        htmlContent: formData.htmlContent,
        textContent: formData.textContent.trim() || null,
        fromName: formData.fromName.trim() || null,
        fromEmail: formData.fromEmail.trim() || null,
        replyToEmail: formData.replyToEmail.trim() || null,
        status: formData.status,
      };

      if (isEditMode && emailLayout && onUpdateEmailLayout) {
        await onUpdateEmailLayout(emailLayout.id || "", submitData);
      } else if (!isEditMode && onCreateEmailLayout) {
        await onCreateEmailLayout(submitData);
      }

      onClose();
    } catch (error) {
      console.error("Email layout submission error:", error);
    }
  };

  // Get the error to display for a field
  const getDisplayError = (field: string): string | null | undefined => {
    return validationErrors[field];
  };

  // Determine title based on mode and loading state
  const getTitle = () => {
    if (isLoading) {
      return "Loading...";
    }
    if (isSystemLayoutItem) {
      return "View System Layout";
    }
    return isEditMode ? "Edit Email Layout" : "Create Email Layout";
  };

  return (
    <AppDrawer
      opened={opened}
      onClose={onClose}
      title={getTitle()}
      position="right"
      size="lg"
      closable={!isSubmitting && !isLoading}
      loading={isSubmitting}
      errors={lastError}
      actionType={
        isLoading ? "close" : isSystemLayoutItem ? "close" : "save-cancel"
      }
      onConfirm={isSystemLayoutItem || isLoading ? undefined : handleSubmit}
    >
      {isLoading ? (
        <FormSkeleton />
      ) : (
        <Stack gap="md">
          {/* System layout notice */}
          {isSystemLayoutItem && (
            <Alert
              icon={<IconInfoCircle size={16} />}
              color="blue"
              variant="light"
            >
              This is a system layout and cannot be modified. You can duplicate
              it to create your own customized version.
            </Alert>
          )}

          {/* Basic Information */}
          <TextInput
            label="Layout Name"
            placeholder="Enter layout name"
            value={formData.name}
            onChange={(e) => handleFieldChange("name", e.target.value)}
            error={getDisplayError("name")}
            required
            withAsterisk
            size="md"
            readOnly={isSystemLayoutItem}
          />

          <Textarea
            label="Description"
            placeholder="Enter layout description (optional)"
            value={formData.description}
            onChange={(e) => handleFieldChange("description", e.target.value)}
            minRows={2}
            maxRows={4}
            autosize
            size="md"
            readOnly={isSystemLayoutItem}
          />

          <Alert
            icon={<IconInfoCircle size={16} />}
            color="gray"
            variant="light"
          >
            <Text size="sm">
              Use <Code>{"{{messageBody}}"}</Code> where the email content
              should be inserted. Other available variables:{" "}
              <Code>{"{{currentYear}}"}</Code>, <Code>{"{{subject}}"}</Code>
            </Text>
          </Alert>

          <Textarea
            label="HTML Content"
            placeholder="Enter HTML template..."
            value={formData.htmlContent}
            onChange={(e) => handleFieldChange("htmlContent", e.target.value)}
            error={getDisplayError("htmlContent")}
            required
            withAsterisk
            minRows={12}
            maxRows={20}
            autosize
            size="md"
            styles={{
              input: {
                fontFamily: "monospace",
                fontSize: "13px",
              },
            }}
            readOnly={isSystemLayoutItem}
          />

          <Textarea
            label="Plain Text Content"
            placeholder="Enter plain text version (optional)..."
            description="Used for email clients that don't support HTML"
            value={formData.textContent}
            onChange={(e) => handleFieldChange("textContent", e.target.value)}
            minRows={6}
            maxRows={12}
            autosize
            size="md"
            styles={{
              input: {
                fontFamily: "monospace",
                fontSize: "13px",
              },
            }}
            readOnly={isSystemLayoutItem}
          />
        </Stack>
      )}
    </AppDrawer>
  );
};
